import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Loader2,
  Lock,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import useUTM, { utmPayload } from "../lib/useUTM";

/**
 * LoanSimulator
 *
 * Interactive auto-loan simulator for the /the-case page. Four sliders drive
 * live numbers: down payment, vehicle loan amount, debt-to-income ratio, and
 * pledge-membership count. Shows dealer-vs-proposed-TDSP-FCU math side by
 * side, plus charter-readiness and approval-likelihood signals.
 *
 * Compliance:
 *   - All forward-looking numbers are illustrative only.
 *   - Dedicated disclaimer baked into the UI.
 *   - "Lock in" form POSTs to /api/the-case/lead which Fernet-encrypts every
 *     financial field at rest (NCUA Part 748 / GLBA 501(b)).
 *
 * Instagram-ready:
 *   - Mobile-first vertical layout.
 *   - Big tabular-nums readouts and the TDSP FCU chrome logo at the top so
 *     screenshots stay on-brand.
 */

/* ─────────────── math (mirrors backend _estimate) ─────────────── */

function dealerApr(dti) {
  if (dti < 20) return 10.9;
  if (dti < 30) return 14.9;
  if (dti < 40) return 18.9;
  if (dti < 50) return 23.9;
  return 28.9;
}
function tdspApr(dti) {
  if (dti < 20) return 5.9;
  if (dti < 30) return 7.9;
  if (dti < 40) return 10.9;
  if (dti < 50) return 13.9;
  return 16.9;
}
function monthlyPayment(principal, apr, n) {
  if (principal <= 0 || n <= 0) return 0;
  const r = apr / 100 / 12;
  if (r <= 0) return principal / n;
  return (r * principal) / (1 - Math.pow(1 + r, -n));
}
function charterTier(pledges) {
  if (pledges >= 3000) return { label: "Charter unlocked", tier: "charter_unlocked" };
  if (pledges >= 2500) return { label: "Final sprint", tier: "final_sprint" };
  if (pledges >= 1500) return { label: "Halfway there", tier: "halfway" };
  if (pledges >= 500) return { label: "Momentum building", tier: "building" };
  return { label: "Early stage", tier: "early" };
}
function approvalScore(dti, downPct) {
  let s = 0;
  if (dti <= 20) s += 50;
  else if (dti <= 35) s += 35;
  else if (dti <= 45) s += 20;
  else s += 5;
  if (downPct >= 20) s += 40;
  else if (downPct >= 10) s += 25;
  else if (downPct >= 5) s += 15;
  else s += 5;
  s = Math.max(0, Math.min(100, s));
  let label;
  if (s >= 80) label = "Very likely";
  else if (s >= 60) label = "Likely";
  else if (s >= 40) label = "Possible with work";
  else label = "Needs preparation";
  return { score: s, label };
}

const money0 = (v) =>
  "$" + Math.round(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
const money2 = (v) =>
  "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ─────────────── UI primitives ─────────────── */

function Slider({ label, value, min, max, step, onChange, format, hint, testid }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <label className="label-eyebrow">{label}</label>
        <div className="font-serif text-2xl text-bdu-cream tabular-nums">
          {format ? format(value) : value}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="simulator-slider mt-3 w-full"
        style={{
          background: `linear-gradient(to right, #D4AF37 0%, #F7D878 ${pct}%, rgba(224,224,224,0.12) ${pct}%, rgba(224,224,224,0.12) 100%)`,
        }}
        data-testid={`${testid}-input`}
      />
      {hint && (
        <div className="mt-1.5 text-[0.65rem] text-bdu-cream/40 uppercase tracking-widest">
          {hint}
        </div>
      )}
    </div>
  );
}

function BigNumber({ label, value, tone = "bdu", testid, sub }) {
  const palette =
    tone === "gold"
      ? "linear-gradient(135deg, #FFFFFF 0%, #F7D878 40%, #D4AF37 100%)"
      : tone === "red"
      ? "linear-gradient(135deg, #FFC2C2 0%, #ff8577 50%, #d14b4b 100%)"
      : "linear-gradient(135deg, #FFFFFF 0%, #E0E0E0 50%, #B0B0B0 100%)";
  return (
    <div className="text-center" data-testid={testid}>
      <div
        className="font-serif text-4xl sm:text-5xl leading-none tabular-nums"
        style={{
          background: palette,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}
      >
        {value}
      </div>
      <div className="mt-2 label-eyebrow">{label}</div>
      {sub && <div className="mt-1 text-xs text-bdu-cream/55">{sub}</div>}
    </div>
  );
}

/* ─────────────── main ─────────────── */

export default function LoanSimulator() {
  const utm = useUTM();
  const [downPayment, setDownPayment] = useState(2000);
  const [loanAmount, setLoanAmount] = useState(22000);
  const [dti, setDti] = useState(38);
  const [pledges, setPledges] = useState(1200);
  const [term, setTerm] = useState(60);

  // lead-capture state
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", zip: "" });
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId] = useState(null);

  const est = useMemo(() => {
    const principal = Math.max(0, loanAmount - downPayment);
    const dApr = dealerApr(dti);
    const tApr = tdspApr(dti);
    const dMo = monthlyPayment(principal, dApr, term);
    const tMo = monthlyPayment(principal, tApr, term);
    const dTotal = dMo * term;
    const tTotal = tMo * term;
    const dInterest = Math.max(0, dTotal - principal);
    const tInterest = Math.max(0, tTotal - principal);
    const savingsMonthly = dMo - tMo;
    const savingsInterest = dInterest - tInterest;
    const downPct = (downPayment / Math.max(1, loanAmount)) * 100;
    return {
      principal,
      dApr,
      tApr,
      dMo,
      tMo,
      dTotal,
      tTotal,
      dInterest,
      tInterest,
      savingsMonthly,
      savingsInterest,
      approval: approvalScore(dti, downPct),
      charter: charterTier(pledges),
      pledgePct: Math.min(100, (pledges / 3000) * 100),
    };
  }, [downPayment, loanAmount, dti, pledges, term]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/the-case/lead", {
        name: lead.name.trim(),
        email: lead.email.trim(),
        phone: lead.phone.trim() || null,
        zip: lead.zip.trim() || null,
        down_payment: downPayment,
        loan_amount: loanAmount,
        dti_pct: dti,
        pledges: pledges,
        term_months: term,
        ...utmPayload(utm),
      });
      setSavedId(r.data.lead_id);
      toast.success("Your estimate is encrypted and locked in.");
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Save failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // reflow when `showLead` opens
  useEffect(() => {
    if (showLead) {
      const el = document.querySelector('[data-testid="simulator-lead-form"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [showLead]);

  return (
    <section
      id="simulator"
      className="relative border-b border-bdu-gold/15"
      data-testid="loan-simulator-section"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% 0%, rgba(212,175,55,0.08) 0%, rgba(0,0,0,0) 55%), linear-gradient(180deg, #050505 0%, #0a0a0a 50%, #050505 100%)",
      }}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-10 py-20">
        {/* HEADER with TDSP FCU logo */}
        <div className="text-center max-w-3xl mx-auto">
          <img
            src="/brand/historic-milestone-hero.png"
            alt="Proposed Trillion Dollar Spending Power Federal Credit Union — chrome logo"
            className="mx-auto mb-8 h-24 sm:h-32 w-auto object-contain"
            data-testid="simulator-tdspfcu-logo"
          />
          <div
            className="inline-flex items-center gap-2 label-eyebrow"
            style={{ color: "rgba(212,175,55,0.9)" }}
          >
            <Sparkles size={14} /> The simulator · Pending NCUA charter approval
          </div>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-[1.05] tracking-tight">
            Slide the numbers.{" "}
            <span
              className="bg-clip-text text-transparent italic"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #F7D878 0%, #D4AF37 50%, #8B6914 100%)",
              }}
            >
              See what we save you.
            </span>
          </h2>
          <p className="mt-5 text-sm sm:text-base text-bdu-cream/70 leading-relaxed max-w-2xl mx-auto">
            Every slider is interactive. Numbers update live. This is an{" "}
            <span className="text-bdu-cream">illustrative preview</span> of what
            member-owned auto financing would look like at the Proposed Trillion Dollar
            Spending Power Federal Credit Union — on NCUA charter approval.
          </p>
        </div>

        {/* MAIN GRID: sliders on left, outputs on right */}
        <div className="mt-14 grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* SLIDERS */}
          <div
            className="lg:col-span-2 space-y-7 bg-bdu-surface2 border border-bdu-gold/20 p-6 sm:p-7"
            data-testid="simulator-sliders"
          >
            <Slider
              testid="simulator-down-payment"
              label="Down payment"
              value={downPayment}
              min={0}
              max={20000}
              step={250}
              format={money0}
              onChange={setDownPayment}
              hint="More skin-in-the-game lifts your approval odds"
            />
            <Slider
              testid="simulator-loan-amount"
              label="Vehicle loan requested"
              value={loanAmount}
              min={5000}
              max={60000}
              step={500}
              format={money0}
              onChange={setLoanAmount}
              hint="Total vehicle price minus trade-in"
            />
            <Slider
              testid="simulator-dti"
              label="Debt-to-income ratio"
              value={dti}
              min={0}
              max={60}
              step={1}
              format={(v) => `${v}%`}
              onChange={setDti}
              hint="Monthly debts ÷ gross monthly income"
            />
            <Slider
              testid="simulator-pledges"
              label="Pledge membership count"
              value={pledges}
              min={0}
              max={3500}
              step={25}
              format={(v) => v.toLocaleString()}
              onChange={setPledges}
              hint="Slide to 3,000 — that's charter unlocked"
            />

            {/* Term — compact chip selector */}
            <div>
              <div className="label-eyebrow mb-2">Loan term</div>
              <div className="flex gap-2 flex-wrap">
                {[36, 48, 60, 72, 84].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setTerm(n)}
                    data-testid={`simulator-term-${n}`}
                    className={`px-3.5 py-1.5 text-xs uppercase tracking-widest transition border ${
                      term === n
                        ? "bg-bdu-gold text-black border-bdu-gold font-semibold"
                        : "bg-black/40 text-bdu-cream/70 border-bdu-cream/15 hover:border-bdu-cream/40"
                    }`}
                  >
                    {n} mo
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* OUTPUTS */}
          <div className="lg:col-span-3 space-y-6" data-testid="simulator-outputs">
            {/* big savings headline */}
            <div
              className="relative overflow-hidden border border-bdu-gold/40 p-7 sm:p-9 text-center"
              style={{
                background:
                  "radial-gradient(500px 240px at 50% 0%, rgba(212,175,55,0.22) 0%, rgba(0,0,0,0) 60%), linear-gradient(135deg, #0a0a0a 0%, #060606 100%)",
              }}
              data-testid="simulator-savings-hero"
            >
              <div className="label-eyebrow text-bdu-gold">You keep in your pocket</div>
              <div
                className="mt-3 font-serif text-6xl sm:text-7xl lg:text-8xl leading-none tabular-nums"
                style={{
                  background:
                    "linear-gradient(135deg, #FFFFFF 0%, #F7D878 40%, #D4AF37 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  filter:
                    "drop-shadow(0 10px 30px rgba(212,175,55,0.25))",
                }}
              >
                {money0(Math.max(0, est.savingsInterest))}
              </div>
              <div className="mt-3 text-sm text-bdu-cream/60">
                over {term} months · vs the dealer deal · plus{" "}
                <span className="text-bdu-cream">
                  {money2(Math.max(0, est.savingsMonthly))}/mo
                </span>{" "}
                lower payments
              </div>
            </div>

            {/* side-by-side comparison */}
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Dealer */}
              <div
                className="bg-black/60 border border-red-400/25 p-5"
                data-testid="simulator-dealer-card"
              >
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow text-red-300/80">Dealer finance desk</div>
                  <div className="text-xs text-bdu-cream/40">illustrative</div>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <div
                    className="font-serif text-3xl text-red-200 tabular-nums"
                    data-testid="simulator-dealer-apr"
                  >
                    {est.dApr.toFixed(1)}%
                  </div>
                  <div className="text-xs uppercase tracking-widest text-bdu-cream/50">APR</div>
                </div>
                <div className="mt-5 space-y-2.5 text-sm">
                  <div className="flex items-baseline justify-between">
                    <span className="text-bdu-cream/60">Monthly</span>
                    <span className="font-mono tabular-nums text-bdu-cream">
                      {money2(est.dMo)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-bdu-cream/60">Total interest</span>
                    <span className="font-mono tabular-nums text-red-300">
                      {money0(est.dInterest)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-bdu-cream/60">You pay in total</span>
                    <span className="font-mono tabular-nums text-bdu-cream">
                      {money0(est.dTotal)}
                    </span>
                  </div>
                </div>
              </div>

              {/* TDSP FCU */}
              <div
                className="bg-black/60 border border-bdu-gold/50 p-5 shadow-[0_20px_60px_-15px_rgba(212,175,55,0.3)]"
                data-testid="simulator-tdsp-card"
              >
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow text-bdu-gold">Proposed TDSP FCU</div>
                  <div className="text-xs text-bdu-gold/60">on charter approval</div>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <div
                    className="font-serif text-3xl text-bdu-gold tabular-nums"
                    data-testid="simulator-tdsp-apr"
                  >
                    {est.tApr.toFixed(1)}%
                  </div>
                  <div className="text-xs uppercase tracking-widest text-bdu-cream/50">APR</div>
                </div>
                <div className="mt-5 space-y-2.5 text-sm">
                  <div className="flex items-baseline justify-between">
                    <span className="text-bdu-cream/60">Monthly</span>
                    <span className="font-mono tabular-nums text-bdu-cream">
                      {money2(est.tMo)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-bdu-cream/60">Total interest</span>
                    <span className="font-mono tabular-nums text-bdu-gold">
                      {money0(est.tInterest)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-bdu-cream/60">You pay in total</span>
                    <span className="font-mono tabular-nums text-bdu-cream">
                      {money0(est.tTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* signal strip: approval + charter */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div
                className="bg-bdu-surface2 border border-bdu-gold/15 p-5"
                data-testid="simulator-approval-card"
              >
                <div className="flex items-center gap-2 label-eyebrow">
                  {est.approval.score >= 60 ? (
                    <CheckCircle2 size={13} className="text-green-300" />
                  ) : (
                    <AlertTriangle size={13} className="text-amber-300" />
                  )}
                  Approval signal
                </div>
                <div className="mt-2 font-serif text-xl text-bdu-cream">
                  {est.approval.label}
                </div>
                <div className="mt-3 h-1.5 bg-bdu-cream/10 overflow-hidden rounded-full">
                  <div
                    className="h-full"
                    style={{
                      width: `${est.approval.score}%`,
                      background:
                        est.approval.score >= 60
                          ? "linear-gradient(90deg, #22c55e, #86efac)"
                          : est.approval.score >= 40
                          ? "linear-gradient(90deg, #D4AF37, #F7D878)"
                          : "linear-gradient(90deg, #ef4444, #fca5a5)",
                      transition: "width 600ms cubic-bezier(0.4,0,0.2,1)",
                    }}
                  />
                </div>
                <div className="mt-2 text-xs text-bdu-cream/40">
                  Illustrative only · not an underwriting decision
                </div>
              </div>

              <div
                className="bg-bdu-surface2 border border-bdu-gold/15 p-5"
                data-testid="simulator-charter-card"
              >
                <div className="flex items-center gap-2 label-eyebrow text-bdu-gold">
                  <TrendingUp size={13} /> Charter readiness
                </div>
                <div className="mt-2 font-serif text-xl text-bdu-cream">
                  {est.charter.label}
                </div>
                <div className="mt-3 h-1.5 bg-bdu-cream/10 overflow-hidden rounded-full">
                  <div
                    className="h-full"
                    style={{
                      width: `${est.pledgePct}%`,
                      background: "linear-gradient(90deg, #F7D878, #D4AF37)",
                      transition: "width 600ms cubic-bezier(0.4,0,0.2,1)",
                    }}
                  />
                </div>
                <div className="mt-2 text-xs text-bdu-cream/40 tabular-nums">
                  {pledges.toLocaleString()} / 3,000 pledge cards
                </div>
              </div>
            </div>

            {/* CTA ROW */}
            <div className="flex flex-wrap items-center justify-center gap-3 pt-3">
              <button
                type="button"
                onClick={() => setShowLead(true)}
                data-testid="simulator-lock-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110 shadow-[0_12px_32px_-8px_rgba(212,175,55,0.55)]"
                style={{
                  background:
                    "linear-gradient(135deg, #F7D878 0%, #D4AF37 50%, #B8860B 100%)",
                  border: "1px solid #B8860B",
                }}
              >
                <Lock size={14} /> Lock In My Estimate (encrypted)
              </button>
              <Link
                to="/credit-union#subscribe"
                className="btn-ghost text-xs"
                data-testid="simulator-pledge-cta"
              >
                Sign Pledge Instead <ArrowRight size={14} />
              </Link>
            </div>

            {/* LEAD FORM (toggles) */}
            {showLead && (
              <form
                onSubmit={submitLead}
                className="bg-bdu-surface2 border border-bdu-gold/30 p-6 mt-4"
                data-testid="simulator-lead-form"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="label-eyebrow">Lock in your estimate</div>
                    <div className="mt-1 text-sm text-bdu-cream/60">
                      Encrypted at rest. We never sell your data.
                    </div>
                  </div>
                  <div className="text-[0.6rem] uppercase tracking-widest text-bdu-cream/50 inline-flex items-center gap-1.5">
                    <Lock size={10} /> AES / HMAC
                  </div>
                </div>

                {savedId ? (
                  <div
                    className="mt-5 bg-black/40 border border-green-400/30 p-5 text-center"
                    data-testid="simulator-lead-success"
                  >
                    <CheckCircle2 size={26} className="mx-auto text-green-300" />
                    <div className="mt-3 font-serif text-xl text-bdu-cream">
                      Encrypted and saved.
                    </div>
                    <div className="mt-2 text-sm text-bdu-cream/60">
                      Reference: <span className="text-bdu-gold font-mono">{savedId.slice(-8)}</span>
                    </div>
                    <Link
                      to="/credit-union#subscribe"
                      className="mt-5 inline-flex items-center gap-2 btn-gold text-xs"
                      data-testid="simulator-lead-pledge-cta"
                    >
                      Sign the Pledge Membership Card <ArrowRight size={14} />
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="mt-5 grid sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        required
                        placeholder="Full name"
                        value={lead.name}
                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                        className="bg-black/40 border border-bdu-gold/20 px-4 py-3 text-sm text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition"
                        data-testid="simulator-lead-name"
                      />
                      <input
                        type="email"
                        required
                        placeholder="Email"
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        className="bg-black/40 border border-bdu-gold/20 px-4 py-3 text-sm text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition"
                        data-testid="simulator-lead-email"
                      />
                      <input
                        type="tel"
                        placeholder="Phone (optional)"
                        value={lead.phone}
                        onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                        className="bg-black/40 border border-bdu-gold/20 px-4 py-3 text-sm text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition"
                        data-testid="simulator-lead-phone"
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d{5}"
                        maxLength={5}
                        placeholder="ZIP"
                        value={lead.zip}
                        onChange={(e) => setLead({ ...lead, zip: e.target.value.replace(/\D/g, "") })}
                        className="bg-black/40 border border-bdu-gold/20 px-4 py-3 text-sm text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition"
                        data-testid="simulator-lead-zip"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-gold w-full mt-4 justify-center"
                      data-testid="simulator-lead-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={14} className="animate-spin" /> Encrypting…
                        </>
                      ) : (
                        <>
                          Lock In My Estimate <Lock size={14} />
                        </>
                      )}
                    </button>
                  </>
                )}
              </form>
            )}

            {/* DISCLAIMER */}
            <div className="mt-6 pt-5 border-t border-bdu-gold/10 text-[0.65rem] uppercase tracking-[0.22em] text-bdu-cream/40 leading-relaxed text-center">
              Illustrative only. The Proposed Trillion Dollar Spending Power Federal Credit Union is pending NCUA charter approval and cannot originate loans. No rate, product, or approval is promised. Actual rates and terms would be set by the credit committee on charter approval. All captured information is encrypted at rest to NCUA Part 748 / GLBA 501(b) standards (AES-128-CBC + HMAC-SHA256).
            </div>
          </div>
        </div>
      </div>

      {/* custom slider styles */}
      <style>{`
        .simulator-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 6px;
          border-radius: 999px;
          outline: none;
        }
        .simulator-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FFFFFF 0%, #F7D878 50%, #D4AF37 100%);
          border: 2px solid #B8860B;
          box-shadow: 0 4px 12px rgba(212,175,55,0.5), 0 0 0 4px rgba(212,175,55,0.08);
          cursor: pointer;
          transition: transform 150ms ease;
        }
        .simulator-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }
        .simulator-slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FFFFFF 0%, #F7D878 50%, #D4AF37 100%);
          border: 2px solid #B8860B;
          box-shadow: 0 4px 12px rgba(212,175,55,0.5);
          cursor: pointer;
        }
      `}</style>
    </section>
  );
}
